import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Plus, UserPlus, ClipboardList, Users, Loader2, Pencil, Mail, Trash2, TrendingUp, BarChart3, FileText, Save, Route } from "lucide-react";
import api from "@/lib/api";
import { CourseBuilder } from "./CourseBuilder";
import { CourseAnalytics } from "./CourseAnalytics";
import { CompanyManager } from "./CompanyManager";
import { LearningPaths } from "./LearningPaths";
import { PassRateTrends } from "@/components/portal/PassRateTrends";

const inputCls = "w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50 transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5";
const subTabCls = (active) =>
  `inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${active ? "border-cadmium/60 bg-cadmium/10 text-sky-600" : "border-slate-200 text-muted-foreground hover:text-slate-900"}`;

const STATUS_BADGE = {
  assigned: "border-white/20 bg-slate-100 text-muted-foreground",
  in_progress: "border-amber/50 bg-amber/10 text-amber-700",
  completed: "border-moss/50 bg-moss/10 text-green-700",
};

export const TrainingAdmin = () => {
  const [sub, setSub] = useState("progress");
  const [progress, setProgress] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [shares, setShares] = useState([]);
  const [building, setBuilding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [empForm, setEmpForm] = useState({ name: "", email: "", password: "", company_id: "", community: "", training_format: "" });
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [empEdit, setEmpEdit] = useState({ name: "", community: "", training_format: "", company_id: "" });
  const [savingEmpEdit, setSavingEmpEdit] = useState(false);
  const startEmpEdit = (e) => {
    setEditingEmpId(e.id);
    setEmpEdit({ name: e.name, community: e.community || "", training_format: e.training_format || "", company_id: e.company_id || "" });
  };

  const saveEmpEdit = async () => {
    setSavingEmpEdit(true);
    try {
      await api.patch(`/training/employees/${editingEmpId}`, empEdit);
      toast.success("Employee updated");
      setEditingEmpId(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Update failed");
    } finally {
      setSavingEmpEdit(false);
    }
  };
  const [savingEmp, setSavingEmp] = useState(false);
  const [assignCourse, setAssignCourse] = useState("");
  const [assignDue, setAssignDue] = useState("");
  const [picks, setPicks] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [notifying, setNotifying] = useState("");

  const deleteEmployee = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their course assignments and progress are deleted too.`)) return;
    try {
      await api.delete(`/training/employees/${id}`);
      toast.success("Employee removed");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Remove failed");
    }
  };

  const notify = async (employeeIds = []) => {
    const key = employeeIds.length ? employeeIds[0] : "all";
    setNotifying(key);
    try {
      const res = await api.post("/training/notify", { employee_ids: employeeIds });
      const d = res.data;
      if (d.emailed.length) {
        toast.success(`Training summary sent to ${d.emailed.length} employee(s)`, {
          description: [
            d.email_failed.length ? `Failed: ${d.email_failed.join(", ")} — address may not be deliverable.` : null,
            d.skipped_no_assignments.length ? `No assignments: ${d.skipped_no_assignments.join(", ")}` : null,
          ].filter(Boolean).join(" ") || undefined,
        });
      } else {
        toast.error("No emails sent", {
          description: d.skipped_no_assignments.length
            ? `${d.skipped_no_assignments.join(", ")} have no assigned courses.`
            : d.email_failed.join(", "),
        });
      }
    } catch {
      toast.error("Notify failed");
    } finally {
      setNotifying("");
    }
  };

  const load = useCallback(async () => {
    try {
      const [p, c, e, s, co] = await Promise.all([
        api.get("/training/admin/progress"),
        api.get("/training/courses"),
        api.get("/training/employees"),
        api.get("/training/shares"),
        api.get("/training/companies"),
      ]);
      setProgress(p.data);
      setCourses(c.data);
      setEmployees(e.data);
      setShares(s.data);
      setCompanies(co.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Admin access required for training manager");
      else toast.error("Could not load training data");
    }
  }, []);

  const setEmployeeCompany = async (id, company_id) => {
    try {
      await api.patch(`/training/employees/${id}`, { company_id });
      toast.success("Company updated");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Update failed");
    }
  };

  useEffect(() => { load(); }, [load]);

  const createEmployee = async () => {
    setSavingEmp(true);
    try {
      const res = await api.post("/training/employees", empForm);
      toast.success(`Employee ${empForm.name} created`, {
        description: res.data.emailed
          ? `Sign-in email and temporary password emailed to ${empForm.email}.`
          : `Account created, but the credentials email to ${empForm.email} could not be delivered — share the password with them directly.`,
      });
      setEmpForm({ name: "", email: "", password: "", company_id: "", community: "", training_format: "" });
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Create failed");
    } finally {
      setSavingEmp(false);
    }
  };

  const assign = async () => {
    if (!assignCourse || picks.size === 0) { toast.error("Pick a course and at least one employee"); return; }
    setAssigning(true);
    try {
      const res = await api.post("/training/assign", { course_id: assignCourse, employee_ids: [...picks], due_date: assignDue || null });
      toast.success(`Assigned to ${res.data.assigned} employee(s)`, {
        description: res.data.email_failed?.length
          ? `Email failed for ${res.data.email_failed.join(", ")} — the address may not be deliverable.`
          : res.data.emailed
            ? "Each employee got an email with a link to the training portal."
            : "Already assigned — no new emails sent.",
      });
      setPicks(new Set());
      load();
    } catch {
      toast.error("Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const editCourse = async (id) => {
    try {
      const res = await api.get(`/training/courses/${id}`);
      setEditing(res.data);
      setBuilding(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not load course");
    }
  };

  const deleteCourse = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? Employee assignments and exam history for this course are removed too.`)) return;
    try {
      await api.delete(`/training/courses/${id}`);
      toast.success("Course deleted");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Delete failed");
    }
  };

  const setDue = async (id, due_date) => {
    try {
      await api.patch(`/training/assignments/${id}`, { due_date: due_date || null });
      toast.success("Due date updated");
      load();
    } catch {
      toast.error("Could not update due date");
    }
  };

  return (
    <div data-testid="training-admin">
      <div className="flex flex-wrap gap-2 mb-6">
        <button data-testid="ta-sub-progress" onClick={() => setSub("progress")} className={subTabCls(sub === "progress")}><ClipboardList size={12} /> Progress</button>
        <button data-testid="ta-sub-courses" onClick={() => setSub("courses")} className={subTabCls(sub === "courses")}><GraduationCap size={12} /> Courses ({courses.length})</button>
        <button data-testid="ta-sub-employees" onClick={() => setSub("employees")} className={subTabCls(sub === "employees")}><Users size={12} /> Employees ({employees.length})</button>
        <button data-testid="ta-sub-paths" onClick={() => setSub("paths")} className={subTabCls(sub === "paths")}><Route size={12} /> Learning Paths</button>
        <button data-testid="ta-sub-shares" onClick={() => setSub("shares")} className={subTabCls(sub === "shares")}><Mail size={12} /> Share Log ({shares.length})</button>
        <button data-testid="ta-sub-trends" onClick={() => setSub("trends")} className={subTabCls(sub === "trends")}><TrendingUp size={12} /> Trends</button>
      </div>

      {sub === "trends" && <PassRateTrends testid="ta-pass-trend-chart" />}

      {sub === "paths" && <LearningPaths courses={courses} employees={employees} />}

      {sub === "progress" && (
        <div className="technical-card overflow-hidden" data-testid="ta-progress-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {["Employee", "Course", "Status", "Progress", "Best Score", "Passed", "Due", "Attempts"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {progress.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">No assignments yet — assign a course from the Courses tab.</td></tr>
                )}
                {progress.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100" data-testid={`ta-progress-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-slate-900 font-medium">{a.employee_name || "—"}</p>
                      <p className="text-[11px] text-muted-foreground">{a.employee_email}</p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{a.course_title}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-sm border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[a.status] || STATUS_BADGE.assigned}`}>{a.status.replace("_", " ")}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{a.progress_pct}%</td>
                    <td className="px-4 py-3 font-mono text-slate-900">{a.best_score != null ? `${a.best_score}%` : "—"}</td>
                    <td className="px-4 py-3">{a.passed ? <span className="text-green-700 font-mono text-xs">YES</span> : <span className="text-muted-foreground/50 font-mono text-xs">—</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="date"
                          defaultValue={a.due_date || ""}
                          data-testid={`ta-due-${a.id}`}
                          style={{ colorScheme: "dark" }}
                          onChange={(e) => setDue(a.id, e.target.value)}
                          className="rounded-sm border border-slate-200 bg-transparent px-1.5 py-1 text-[11px] font-mono text-muted-foreground focus:outline-none focus:border-cadmium/50"
                        />
                        {a.overdue && <span className="rounded-sm border border-amber/50 bg-amber/10 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-amber-700">overdue</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">
                      {a.attempts}
                      {a.attempts > 0 && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/training/exam-sheet/${a.id}.pdf`} target="_blank" rel="noreferrer" data-testid={`ta-exam-sheet-${a.id}`} title="Scored exam sheet (PDF)" className="ml-2 inline-flex align-middle text-sky-600 hover:text-white transition-colors">
                          <FileText size={13} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sub === "courses" && (
        <div>
          {!building && !editing && (
            <button data-testid="ta-new-course" onClick={() => setBuilding(true)} className="mb-5 inline-flex items-center gap-2 rounded-md bg-cadmium px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors">
              <Plus size={14} /> New Course
            </button>
          )}
          {building && !editing && <CourseBuilder onCreated={() => { setBuilding(false); load(); }} onCancel={() => setBuilding(false)} />}
          {editing && <CourseBuilder key={editing.id} initial={editing} onCreated={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <div key={c.id} className="technical-card p-5" data-testid={`ta-course-${c.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-sky-600">{c.category}</span>
                  <span className={`rounded-sm border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${c.published ? "border-moss/40 bg-moss/10 text-green-700" : "border-white/20 bg-slate-100 text-muted-foreground"}`}>
                    {c.published ? "Live" : "Draft"}
                  </span>
                </div>
                <p className="font-cabinet text-base font-bold text-slate-900">{c.title}</p>
                <p className="mt-2 text-[11px] font-mono text-muted-foreground">{c.modules} modules · {c.lessons} lessons · {c.questions} exam Q · pass {c.pass_mark}%</p>
                <div className="mt-3 flex items-center gap-4">
                  <button data-testid={`ta-edit-${c.id}`} onClick={() => editCourse(c.id)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-sky-600 hover:text-white transition-colors">
                    <Pencil size={12} /> Edit
                  </button>
                  <button data-testid={`ta-analytics-${c.id}`} onClick={() => setAnalyticsFor(analyticsFor === c.id ? null : c.id)} className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${analyticsFor === c.id ? "text-sky-600" : "text-muted-foreground hover:text-sky-600"}`}>
                    <BarChart3 size={12} /> Analytics
                  </button>
                  <button data-testid={`ta-delete-${c.id}`} onClick={() => deleteCourse(c.id, c.title)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-red-600 transition-colors">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {analyticsFor && <CourseAnalytics key={analyticsFor} courseId={analyticsFor} />}

          <div className="technical-card p-6 mt-8" data-testid="ta-assign-panel">
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-4">Assign Course to Employees</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelCls}>Course</label>
                <select data-testid="ta-assign-course" className={inputCls} value={assignCourse} onChange={(e) => setAssignCourse(e.target.value)}>
                  <option value="">Select a course…</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
                <label className={`${labelCls} mt-3`}>Due date (optional)</label>
                <input type="date" data-testid="ta-assign-due" className={inputCls} style={{ colorScheme: "dark" }} value={assignDue} onChange={(e) => setAssignDue(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Employees</label>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 max-h-40 overflow-y-auto space-y-1.5">
                  {employees.length === 0 && <p className="text-xs text-muted-foreground">No employees yet — create one in the Employees tab.</p>}
                  {employees.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`ta-assign-pick-${e.id}`}
                        checked={picks.has(e.id)}
                        onChange={() => { const next = new Set(picks); next.has(e.id) ? next.delete(e.id) : next.add(e.id); setPicks(next); }}
                        className="accent-[#2E9CFF]"
                      />
                      {e.name} <span className="text-[11px] text-muted-foreground/60">{e.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <button data-testid="ta-assign-submit" onClick={assign} disabled={assigning} className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60">
              {assigning ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />} Assign
            </button>
          </div>
        </div>
      )}

      {sub === "shares" && (
        <div className="technical-card overflow-hidden" data-testid="ta-share-log">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {["Employee", "Course", "Certificate", "Emailed To", "Sent"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shares.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">No certificates shared yet — employees share from the course player after passing.</td></tr>
                )}
                {shares.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100" data-testid={`ta-share-row-${s.id}`}>
                    <td className="px-4 py-3 text-slate-900 font-medium">{s.employee_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.course_title}</td>
                    <td className="px-4 py-3 font-mono text-xs text-sky-600">{s.certificate_id}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.to}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{(s.at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sub === "employees" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="technical-card p-6" data-testid="ta-employee-form">
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-1">Create Employee Account</p>
            <p className="text-[11px] text-muted-foreground mb-5">They sign in at /login and land directly on the training hub. Their sign-in email and temporary password are emailed to them automatically.</p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Full name</label>
                <input data-testid="ta-emp-name" className={inputCls} value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} placeholder="Jordan Pike" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="ta-emp-email" type="email" className={inputCls} value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} placeholder="employee@company.com" />
              </div>
              <div>
                <label className={labelCls}>Temporary password (min 8 chars)</label>
                <input data-testid="ta-emp-password" className={inputCls} value={empForm.password} onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} placeholder="••••••••" />
              </div>
              <div>
                <label className={labelCls}>Company (optional)</label>
                <select data-testid="ta-emp-company" className={inputCls} value={empForm.company_id} onChange={(e) => setEmpForm({ ...empForm, company_id: e.target.value })}>
                  <option value="">No company</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Community or office (optional — prints on exam record)</label>
                <input data-testid="ta-emp-community" className={inputCls} value={empForm.community} onChange={(e) => setEmpForm({ ...empForm, community: e.target.value })} placeholder="Maple Grove MHC" />
              </div>
              <div>
                <label className={labelCls}>Training format (optional — prints on exam record)</label>
                <select data-testid="ta-emp-format" className={inputCls} value={empForm.training_format} onChange={(e) => setEmpForm({ ...empForm, training_format: e.target.value })}>
                  <option value="">Not set</option>
                  <option value="In person">In person</option>
                  <option value="Virtual">Virtual</option>
                  <option value="Video">Video</option>
                </select>
              </div>
              <button data-testid="ta-emp-create" onClick={createEmployee} disabled={savingEmp} className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60">
                {savingEmp ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create Account
              </button>
            </div>
          </div>
          <div className="technical-card p-6" data-testid="ta-employee-list">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Team Members</p>
              <button data-testid="ta-notify-all" onClick={() => notify()} disabled={notifying !== "" || employees.length === 0} className="inline-flex items-center gap-1.5 rounded-md border border-cadmium/40 bg-cadmium/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-sky-600 hover:bg-cadmium/20 transition-colors disabled:opacity-50">
                {notifying === "all" ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Notify All
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3 -mt-2">Emails each employee their assigned courses, due dates, progress, and certificate status for completed courses.</p>

            <CompanyManager />

            {employees.length === 0 && <p className="text-sm text-muted-foreground">No employee accounts yet.</p>}
            <div className="space-y-4">
              {[
                ...companies.map((c) => ({ id: c.id, name: c.name, members: employees.filter((e) => e.company_id === c.id) })),
                { id: "", name: "No company", members: employees.filter((e) => !e.company_id || !companies.some((c) => c.id === e.company_id)) },
              ].filter((g) => g.members.length > 0).map((g) => {
                const co = companies.find((c) => c.id === g.id);
                return (
                <div key={g.id || "none"} data-testid={`ta-emp-group-${g.id || "none"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-sky-600">
                      {g.name} · {g.members.length}{co ? ` · ${co.open_courses} open · avg ${co.avg_score != null ? `${co.avg_score}%` : "—"}` : ""}
                    </p>
                    {g.id && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/training/company/transcripts.pdf?company_id=${g.id}`} target="_blank" rel="noreferrer" title="Export every transcript in this company as one PDF" data-testid={`ta-export-transcripts-${g.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-sky-600 hover:text-sky-800 transition-colors">
                        <GraduationCap size={11} /> Export all
                      </a>
                    )}
                  </div>
                  <div className="space-y-2">
                    {g.members.map((e) => (
                      <div key={e.id} className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`ta-emp-row-${e.id}`}>
                        {editingEmpId === e.id ? (
                          <div className="space-y-2" data-testid={`ta-emp-edit-form-${e.id}`}>
                            <div className="grid sm:grid-cols-2 gap-2">
                              <input className={inputCls} value={empEdit.name} onChange={(ev) => setEmpEdit({ ...empEdit, name: ev.target.value })} placeholder="Full name" data-testid={`ta-emp-edit-name-${e.id}`} />
                              <input className={inputCls} value={empEdit.community} onChange={(ev) => setEmpEdit({ ...empEdit, community: ev.target.value })} placeholder="Community or office" data-testid={`ta-emp-edit-community-${e.id}`} />
                              <select className={inputCls} value={empEdit.training_format} onChange={(ev) => setEmpEdit({ ...empEdit, training_format: ev.target.value })} data-testid={`ta-emp-edit-format-${e.id}`}>
                                <option value="">Format: not set</option>
                                <option value="In person">In person</option>
                                <option value="Virtual">Virtual</option>
                                <option value="Video">Video</option>
                              </select>
                              <select className={inputCls} value={empEdit.company_id} onChange={(ev) => setEmpEdit({ ...empEdit, company_id: ev.target.value })} data-testid={`ta-emp-edit-company-${e.id}`}>
                                <option value="">No company</option>
                                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                            </div>
                            <div className="flex gap-3">
                              <button onClick={saveEmpEdit} disabled={savingEmpEdit} className="inline-flex items-center gap-1.5 rounded-md bg-cadmium px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60" data-testid={`ta-emp-edit-save-${e.id}`}>
                                {savingEmpEdit ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                              </button>
                              <button onClick={() => setEditingEmpId(null)} className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-slate-900 transition-colors" data-testid={`ta-emp-edit-cancel-${e.id}`}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900">{e.name}</p>
                              <p className="text-[11px] text-muted-foreground">{e.email}</p>
                              {(e.community || e.training_format) && (
                                <p className="text-[10px] font-mono text-muted-foreground/60 mt-0.5">{[e.community, e.training_format].filter(Boolean).join(" · ")}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <select
                                data-testid={`ta-emp-company-select-${e.id}`}
                                value={e.company_id || ""}
                                onChange={(ev) => setEmployeeCompany(e.id, ev.target.value)}
                                className="rounded-sm border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-mono text-muted-foreground focus:outline-none focus:border-cadmium/50"
                              >
                                <option value="">No company</option>
                                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/training/employees/${e.id}/transcript.pdf`} target="_blank" rel="noreferrer" title="Training transcript (PDF)" data-testid={`ta-transcript-${e.id}`} className="text-muted-foreground hover:text-sky-600 transition-colors">
                                <GraduationCap size={12} />
                              </a>
                              <button data-testid={`ta-notify-${e.id}`} onClick={() => notify([e.id])} disabled={notifying !== ""} title="Email assigned courses" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-sky-600 transition-colors disabled:opacity-50">
                                {notifying === e.id ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Notify
                              </button>
                              <button data-testid={`ta-emp-edit-${e.id}`} onClick={() => startEmpEdit(e)} title="Edit employee" className="text-muted-foreground hover:text-sky-600 transition-colors">
                                <Pencil size={12} />
                              </button>
                              <button data-testid={`ta-emp-delete-${e.id}`} onClick={() => deleteEmployee(e.id, e.name)} title="Remove employee" className="text-muted-foreground hover:text-red-600 transition-colors">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
